// Angular
import { LiveAnnouncer } from '@angular/cdk/a11y';
import { NgTemplateOutlet } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  inject,
  input,
  linkedSignal,
  output,
  signal,
  TemplateRef,
  viewChild,
} from '@angular/core';
// Angular Material
import { MatButtonModule } from '@angular/material/button';
import {
  MatCheckboxChange,
  MatCheckboxModule,
} from '@angular/material/checkbox';
import { MatIconModule } from '@angular/material/icon';
import { PageEvent } from '@angular/material/paginator';
import { Sort } from '@angular/material/sort';
import { MatTableModule } from '@angular/material/table';
import { RouterLink } from '@angular/router';

// Component Library
import { FsxPaginatorsComponent, FsxTableColumn } from 'fsx-component-library';

// Local
import { STANDARD_SORT_IMPORTS } from '@app/directive/sort-header-a11y.directive';
import { UtcToTimezoneDatePipe } from '@app/pipes/utc-to-timezone-date.pipe';
import {
  JudgeReviewQueueCase,
  JudgeReviewQueueDocument,
  JudgeReviewQueueRow,
} from '../models/judge-review-queue-row.model';
import { ROUTER_OUTLETS, ROUTES } from '@routes';
import { formatJudgeReviewStatus } from '../services/map-judge-review-queue.utils';

/**
 * Sort direction for the grid's client-side column sort. Local to the presenter
 * so the grid carries no `@data-contracts` dependency.
 */
export enum GridSortDirection {
  Ascending = 'Ascending',
  Descending = 'Descending',
}

/**
 * Overrides the grid's default Status column (the judge-review status). Judge
 * Chambers shows the clerk review status of each document instead.
 */
export interface GridStatusColumn {
  title: string;
  sortableField?: string;
  content: TemplateRef<{ $implicit: JudgeReviewQueueRow }>;
}

/**
 * The Judge Review queue grid, shared with Judge Chambers. Judge Chambers adds
 * its own columns and cell content through the extension inputs
 * (`columnsBeforeStatus`, `statusColumn`, `columnsAfterStatus`, `caseExtra`,
 * `documentExtra`) and the link builders (`caseLink`); the grid stays a pure
 * presenter.
 */
@Component({
  selector: 'app-judge-review-filings-grid',
  standalone: true,
  imports: [
    NgTemplateOutlet,
    MatButtonModule,
    MatCheckboxModule,
    MatIconModule,
    STANDARD_SORT_IMPORTS,
    MatTableModule,
    RouterLink,
    FsxPaginatorsComponent,
    UtcToTimezoneDatePipe,
  ],
  templateUrl: './judge-review-filings-grid.component.html',
  styleUrls: ['./judge-review-filings-grid.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class JudgeReviewFilingsGridComponent {
  /**
   * The filing data to render in the table (one judge's filings).
   */
  readonly filings = input<JudgeReviewQueueRow[]>([]);

  /** Title of the Case Number / Name column. */
  readonly caseColumnTitle = input('Case Number / Name & Court');

  /**
   * Router link target for a row's lead case, or null to show it as plain text.
   * Defaults to the Case History sidecar for a case with a unique id.
   */
  readonly caseLink = input<(row: JudgeReviewQueueRow) => unknown[] | null>(
    row => this.defaultCaseLink(row),
  );

  /** Replaces the default Status column. */
  readonly statusColumn = input<GridStatusColumn | null>(null);

  /** Extra columns inserted between Doc Title / Size and Status. */
  readonly columnsBeforeStatus = input<FsxTableColumn[]>([]);

  /** Extra columns appended after Status. */
  readonly columnsAfterStatus = input<FsxTableColumn[]>([]);

  /** Content shown before the lead case number (e.g. a case notes button). */
  readonly caseExtra = input<TemplateRef<{
    $implicit: JudgeReviewQueueCase;
  }> | null>(null);

  /** Content shown before a document's title (e.g. a document notes button). */
  readonly documentExtra = input<TemplateRef<{
    $implicit: JudgeReviewQueueDocument;
  }> | null>(null);

  /**
   * Emits the currently selected filing rows whenever the selection changes,
   * so the page-level Actions menu can operate on them.
   */
  readonly selectionChange = output<JudgeReviewQueueRow[]>();

  /**
   * Emits the id of the document to open when a document title is clicked. The
   * container owns the document-loading side effect, keeping this grid a pure
   * presenter.
   */
  readonly documentOpen = output<number>();

  /**
   * Emits the row whose Transaction link was activated. The link itself navigates
   * to the transaction (Review & Sign) view; the container uses this to record the
   * current filing for next / previous navigation.
   */
  readonly transactionOpen = output<JudgeReviewQueueRow>();

  /**
   * Resets selection and paging whenever the underlying data is replaced
   * (judge switch / filter change). Replaces the previous `ngOnChanges` hook now
   * that `filings` is a signal input.
   */
  private readonly resetOnFilingsChange = effect(() => {
    // Register the dependency so the effect re-runs when the rows change, then
    // clear the selection + reset the Material paginator (true side effects on
    // non-Angular sub-systems). The `selectionChange` output is NOT emitted here:
    // outputs fire only from user-driven handlers; the container resets its own
    // selection when the data source changes.
    this.filings();
    this.selectedDocuments.clear();
    // pageIndex resets via linkedSignal (source: filings); only the non-Angular
    // teardown — the Material paginator — belongs in the effect.
    this.paginators()?.setPageIndex({ pageIndex: 0 });
  });

  /**
   * The set of selected document IDs (as strings) — the single source of truth
   * for selection. Row / master checkbox state is *derived* from this set (a row
   * counts as selected when any of its documents is), rather than kept in a
   * parallel row-selection store; that avoids row toggles silently clobbering a
   * judge's per-document choices.
   */
  selectedDocuments: Set<string> = new Set();

  /*********************************************************************************************************************
   * Template refs (viewChild signals) — consumed by the `displayTableColumns`
   * computed so the column list is derived reactively, with no `@ViewChild`
   * decorator, `ngAfterViewInit`, or manual `detectChanges()`.
   ********************************************************************************************************************/
  private readonly transactionTemplate = viewChild<
    TemplateRef<{ $implicit: JudgeReviewQueueRow }>
  >('transactionTemplate');
  private readonly dateTimeTemplate =
    viewChild<TemplateRef<{ $implicit: JudgeReviewQueueRow }>>(
      'dateTimeTemplate',
    );
  private readonly caseNumberTemplate =
    viewChild<TemplateRef<{ $implicit: JudgeReviewQueueRow }>>(
      'caseNumberTemplate',
    );
  private readonly authorizerTemplate =
    viewChild<TemplateRef<{ $implicit: JudgeReviewQueueRow }>>(
      'authorizerTemplate',
    );
  private readonly documentTypeTemplate = viewChild<
    TemplateRef<{ $implicit: JudgeReviewQueueRow }>
  >('documentTypeTemplate');
  private readonly documentTitleTemplate = viewChild<
    TemplateRef<{ $implicit: JudgeReviewQueueRow }>
  >('documentTitleTemplate');
  private readonly documentStatusTemplate = viewChild<
    TemplateRef<{ $implicit: JudgeReviewQueueRow }>
  >('documentStatusTemplate');

  /**
   * The top+bottom paginators wrapping the table. Used to reset the page index
   * when the underlying data is replaced (judge switch / filter change).
   */
  readonly paginators = viewChild<FsxPaginatorsComponent>('paginators');

  /**
   * All available column definitions, derived from the template refs. Returns an
   * empty list until the view (and its template refs) has initialized.
   */
  readonly displayTableColumns = computed<FsxTableColumn[]>(() => {
    const transaction = this.transactionTemplate();
    const dateTime = this.dateTimeTemplate();
    const caseNumber = this.caseNumberTemplate();
    const authorizer = this.authorizerTemplate();
    const documentType = this.documentTypeTemplate();
    const documentTitle = this.documentTitleTemplate();
    const documentStatus = this.documentStatusTemplate();
    const statusOverride = this.statusColumn();
    if (
      !transaction ||
      !dateTime ||
      !caseNumber ||
      !authorizer ||
      !documentType ||
      !documentTitle ||
      !documentStatus
    ) {
      return [];
    }
    // Column order matches the mock: Transaction first; Court is folded into the
    // Case Number / Name column (no standalone Court column).
    return [
      {
        title: 'Transaction',
        content: transaction,
        context: {},
        sortableField: 'filingId',
      },
      {
        title: 'Date / Time',
        content: dateTime,
        context: {},
        sortableField: 'authorizedDate',
      },
      {
        title: this.caseColumnTitle(),
        content: caseNumber,
        context: {},
        sortableField: 'cases[0].name',
      },
      {
        title: 'Authorizer / Org',
        content: authorizer,
        context: {},
        sortableField: 'authorizerName',
      },
      {
        title: 'Document Type',
        content: documentType,
        context: {},
        sortableField: 'documentType',
      },
      {
        title: 'Doc Title / Size',
        content: documentTitle,
        context: {},
      },
      ...this.columnsBeforeStatus(),
      {
        title: statusOverride?.title ?? 'Status',
        content: statusOverride?.content ?? documentStatus,
        context: {},
        sortableField: statusOverride
          ? statusOverride.sortableField
          : 'judgeReviewStatus',
      },
      ...this.columnsAfterStatus(),
    ];
  });

  /**
   * The current sort field. Defaults to newest-first by authorized date — the
   * grid is the single owner of sorting (the store passes filtered rows through
   * unsorted), so this default reproduces the previous store-level default sort.
   * Empty string means no sort applied.
   */
  readonly sortBy = signal<string>('authorizedDate');

  /**
   * The current sort direction. Defaults to descending to pair with the
   * `authorizedDate` default (newest first).
   */
  readonly sortOrder = signal<GridSortDirection>(GridSortDirection.Descending);

  private readonly liveAnnouncer = inject(LiveAnnouncer);

  /** Sort direction in MatSort's vocabulary; empty while no sort is applied. */
  readonly matSortDirection = computed<'asc' | 'desc' | ''>(() => {
    if (!this.sortBy()) {
      return '';
    }
    return this.sortOrder() === GridSortDirection.Ascending ? 'asc' : 'desc';
  });

  /**
   * Current paginator page index. A linkedSignal so it resets to the first page
   * whenever the underlying data is replaced (judge switch / filter change) —
   * the preferred reset-on-source-change idiom over writing it from an effect.
   */
  readonly pageIndex = linkedSignal({
    source: this.filings,
    computation: () => 0,
  });

  /**
   * Current paginator page size.
   */
  readonly pageSize = signal(25);

  /**
   * Available page sizes for the paginator.
   */
  readonly pageSizeOptions = [10, 25, 50, 100];

  /**
   * Currently selected rows — derived from {@link selectedDocuments}: any row
   * with at least one selected document.
   */
  get selectedRows(): JudgeReviewQueueRow[] {
    return this.filings().filter(row => this.isRowSelected(row));
  }

  /**
   * The mat-table column keys, in display order (status indicator + selection
   * checkbox, then the dynamic columns). Derived from the column list so it
   * updates reactively once the template refs resolve.
   */
  readonly displayedColumnKeys = computed<string[]>(() => [
    '__status',
    '__select',
    ...this.displayTableColumns().map(column => column.title),
  ]);

  /**
   * The filings sorted by the current sort state (client-side). Memoized so it
   * only recomputes when the rows or sort state actually change.
   */
  readonly sortedFilings = computed<JudgeReviewQueueRow[]>(() => {
    const data = this.filings() ?? [];
    const sortBy = this.sortBy();
    if (!sortBy) return data;

    const ascending = this.sortOrder() === GridSortDirection.Ascending;
    return [...data].sort((a, b) => {
      const aVal = this.getSortValue(a, sortBy);
      const bVal = this.getSortValue(b, sortBy);

      // Numbers numerically; text case-insensitively (so "apple" sorts before "Zebra").
      const order =
        typeof aVal === 'number' && typeof bVal === 'number'
          ? aVal - bVal
          : String(aVal ?? '').localeCompare(String(bVal ?? ''), undefined, {
              sensitivity: 'base',
            });
      return ascending ? order : -order;
    });
  });

  /**
   * The filings currently visible on the active page. Memoized over the sorted
   * rows and the current page index / size.
   */
  readonly pagedFilings = computed<JudgeReviewQueueRow[]>(() => {
    const start = this.pageIndex() * this.pageSize();
    return this.sortedFilings().slice(start, start + this.pageSize());
  });

  /**
   * Applies MatSort's state (column header click / keyboard) to the grid's sort
   * signals and announces the change. Sorting itself stays client-side over all
   * rows (not just the visible page); a cleared sort shows the rows unsorted.
   *
   * @param sort The new Material sort state.
   */
  onSortChange(sort: Sort): void {
    this.sortBy.set(sort.direction ? sort.active : '');
    this.sortOrder.set(
      sort.direction === 'desc'
        ? GridSortDirection.Descending
        : GridSortDirection.Ascending,
    );

    if (sort.direction) {
      this.liveAnnouncer.announce(`Sorted ${sort.direction}ending`);
    } else {
      this.liveAnnouncer.announce('Sorting cleared');
    }
  }

  /**
   * Whether every visible row is currently selected.
   */
  isAllSelected(): boolean {
    const pageRows = this.pagedFilings();
    return (
      pageRows.length > 0 && pageRows.every(row => this.isRowSelected(row))
    );
  }

  /**
   * Whether at least one row is currently selected.
   */
  isAnySelected(): boolean {
    return this.pagedFilings().some(row => this.isRowSelected(row));
  }

  /**
   * Toggle every visible row on/off based on current state, by selecting or
   * deselecting all of their documents (the authoritative store).
   */
  onMasterToggle(): void {
    const select = !this.isAllSelected();
    this.pagedFilings().forEach(row => this.setRowDocuments(row, select));
    this.emitSelectionChange();
  }

  /**
   * A handler for paginator page changes.
   */
  onPageChanged(event: PageEvent): void {
    this.pageIndex.set(event.pageIndex);
    this.pageSize.set(event.pageSize);
  }

  /**
   * Toggle a single row's selection by selecting / deselecting all of its
   * documents. A row is "on" when any of its documents is selected.
   */
  onRowToggle(row: JudgeReviewQueueRow): void {
    this.setRowDocuments(row, !this.isRowSelected(row));
    this.emitSelectionChange();
  }

  /**
   * Whether a given row is selected — derived: any of its documents is selected.
   */
  isRowSelected(row: JudgeReviewQueueRow): boolean {
    return (row.documents ?? []).some(doc => this.isDocumentSelected(doc));
  }

  /** Selects or deselects every document in a row in the authoritative set. */
  private setRowDocuments(row: JudgeReviewQueueRow, selected: boolean): void {
    (row.documents ?? []).forEach(doc => {
      const docId = doc.documentId?.toString();
      if (!docId) {
        return;
      }
      if (selected) {
        this.selectedDocuments.add(docId);
      } else {
        this.selectedDocuments.delete(docId);
      }
    });
  }

  /** Emits the rows that currently have at least one selected document. */
  private emitSelectionChange(): void {
    this.selectionChange.emit(this.selectedRows);
  }

  /**
   * Formats the document size from bytes to KB.
   *
   * @param docSize The document size in bytes.
   */
  /**
   * Status-column label for a queue row (Pending / In Progress).
   */
  readonly judgeReviewStatusLabel = formatJudgeReviewStatus;

  formatDocumentSize(docSize: number | null | undefined): string {
    if (docSize == null || docSize < 0) {
      return '';
    }
    return (docSize / 1024).toFixed(2) + ' KB';
  }

  /** Router link target for a row's Transaction link (the Review & Sign view). */
  transactionLink(row: JudgeReviewQueueRow): string[] {
    return ['/', ROUTES.JUDGE_REVIEW, row.filingUniqueId ?? ''];
  }

  /**
   * Called when the Transaction link is activated. The link navigates on its own;
   * this lets the container record the current filing.
   */
  onTransactionClicked(row: JudgeReviewQueueRow): void {
    this.transactionOpen.emit(row);
  }

  /** The default {@link caseLink}: Case History for a case with a unique id. */
  private defaultCaseLink(row: JudgeReviewQueueRow): unknown[] | null {
    const caseUniqueId = row.cases[0]?.caseUniqueId;
    return caseUniqueId ? this.caseHistoryLink(caseUniqueId) : null;
  }

  /**
   * Router link target that opens the Case History sidecar for a case (keyed by
   * its GUID), relative to the queue route. The template only renders the link
   * when the case has a unique id.
   */
  caseHistoryLink(caseUniqueId: string): unknown[] {
    return [
      {
        outlets: {
          [ROUTER_OUTLETS.SIDEPANEL_CASE_HISTORY]: `case-history/${caseUniqueId}`,
        },
      },
    ];
  }

  /**
   * A handler function for when the document title link is clicked.
   *
   * @param document The document to open.
   */
  onDocumentTitleClicked(document: JudgeReviewQueueDocument): void {
    if (document.documentId != null) {
      this.documentOpen.emit(document.documentId);
    }
  }

  /**
   * A handler function for when a document checkbox is changed. Updates only the
   * single document in the authoritative set; row / master state is derived, so
   * there is no parallel store to reconcile.
   *
   * @param event The checkbox change event.
   * @param document The document being checked/unchecked.
   */
  onDocumentCheckboxChanged(
    event: MatCheckboxChange,
    document: JudgeReviewQueueDocument,
  ): void {
    const documentId = document.documentId?.toString();
    if (!documentId) {
      return;
    }

    if (event.checked) {
      this.selectedDocuments.add(documentId);
    } else {
      this.selectedDocuments.delete(documentId);
    }

    this.emitSelectionChange();
  }

  /**
   * Checks if a document is selected.
   *
   * @param document The document to check.
   */
  isDocumentSelected(document: JudgeReviewQueueDocument): boolean {
    const documentId = document.documentId?.toString();
    return documentId != null && this.selectedDocuments.has(documentId);
  }

  /**
   * Extracts a sortable primitive value from a row for the given sort field.
   *
   * @param row The row to extract the sort value from.
   * @param field The sort field key.
   */
  private getSortValue(
    row: JudgeReviewQueueRow,
    field: string,
  ): string | number {
    switch (field) {
      case 'courtName':
        return row.courtName ?? '';
      case 'filingId':
        return row.filingId ?? 0;
      case 'authorizedDate':
        return row.authorizedDate ?? '';
      case 'judgeName':
        return row.cases[0]?.judgeLastName ?? '';
      case 'cases[0].name':
        return row.cases[0]?.name ?? '';
      case 'authorizerName':
        return row.authorizerLastName;
      case 'documentType':
        return row.documents[0]?.filingType ?? '';
      case 'judgeReviewStatus':
        return formatJudgeReviewStatus(row.judgeReviewStatus);
      case 'reviewStatus':
        return row.documents[0]?.reviewStatus ?? '';
      default:
        return '';
    }
  }
}
